import { DEFAULT_CURRENCY } from 'core/constants/currency.constants';
import { INSIDER_ID, INSIDER_URL } from 'core/constants/general.constants';
import { INSIDER_STANDARD_TYPES } from 'core/constants/insider.constants';
import { EInsiderEvents, EInsiderPageType } from 'core/model/enums/insider.enum';
import type { IBusiness } from 'core/model/interfaces/business.interface';
import type { IUser } from 'core/model/interfaces/customer.interface';
import type {
  IInsiderCategoryValue,
  IInsiderOtherPageValue,
  IInsiderQueueItem,
  IInsiderSearchPreviewItemClickValue,
  IInsiderUser,
} from 'core/model/interfaces/insider.interface';
import type { IOrderCreated } from 'core/model/interfaces/order.interface';
import type { IBaseProduct, ICartProduct, IProductDetail } from 'core/model/interfaces/product.interface';
import store from 'core/store';
import { isLocalEnv } from './env.utils';
import {
  baseProductToInsiderProduct,
  cartToInsiderCart,
  cutomerToInsiderUser,
  orderDetailToInsiderPurchase,
  productDetailToInsiderProduct,
} from './insider.mapper';

const globalCurrency = DEFAULT_CURRENCY.id;
let insiderUser: IInsiderUser | null = null;

export const initializeInsiderQueue = () => {
  if (typeof window !== 'undefined') {
    window.InsiderQueue = window.InsiderQueue || [];

    if (isLocalEnv) {
      // eslint-disable-next-line no-console
      console.log('[Insider] InsiderQueue inicializado');
    }
  }
};

const pushToInsiderQueue = (item: IInsiderQueueItem) => {
  if (typeof window === 'undefined' || !window.InsiderQueue) {
    // eslint-disable-next-line no-console
    console.warn('[Insider] InsiderQueue no está inicializado');
    return;
  }

  window.InsiderQueue.push(item);

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log(`[Insider] Push ${item.type}:`, item.value);
  }
};

export const initializeInsider = () => {
  pushToInsiderQueue({ type: 'init' });

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log('[Insider] Inicialización completada');
  }
};

export const pushInsiderUser = (customer: IUser, customerId: string, addresses: Array<IBusiness>) => {
  insiderUser = insiderUser || cutomerToInsiderUser(customer, addresses);

  pushToInsiderQueue({
    type: 'user',
    value: insiderUser,
  });

  if (!globalThis.customer_id) globalThis.customer_id = customerId;

  localStorage.setItem('user_document_id', customer.basicData.document.number);

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log('[Insider] Usuario actualizado:', insiderUser);
  }
};

export const pushInsiderCurrency = () => {
  pushToInsiderQueue({
    type: 'currency',
    value: globalCurrency,
  });

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log('[Insider] Moneda actualizada:', globalCurrency);
  }
};

/**
 * Send page view to Insider
 *
 * 1. Send user
 * 2. Send currency
 * 3. Send page type
 * 4. Send init
 *
 * @param {EInsiderPageType} pageType Page type
 * @param {Record<string, any>} pageValue Page value
 */
const sendInsiderPageView = (pageType: EInsiderPageType, pageValue?: Record<string, any>) => {
  const { customer } = store.getState().auth;
  const customerId = customer?.id || '';
  const { addresses } = store.getState().addresses;

  pushInsiderUser(customer, customerId, addresses);

  pushInsiderCurrency();

  const isStandardType = INSIDER_STANDARD_TYPES.includes(pageType);

  if (isStandardType) {
    pushToInsiderQueue({
      type: pageType as IInsiderQueueItem['type'],
      value: pageValue || {},
    });
  } else {
    pushToInsiderQueue({
      type: 'other',
      value: {
        name: pageType,
        ...(pageValue || {}),
      },
    });
  }

  pushToInsiderQueue({ type: 'init' });

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log(`[Insider] Page view enviado: ${pageType}`, { user: !!insiderUser, currency: globalCurrency });
  }
};

export const pushInsiderHomePage = (customData?: Record<string, any>) => {
  sendInsiderPageView(EInsiderPageType.HOME, customData ? { custom: customData } : {});
};

export const pushInsiderCategoryPage = (
  breadcrumb: IInsiderCategoryValue['breadcrumb'],
  customData?: IInsiderCategoryValue['custom'],
) => {
  const value: IInsiderCategoryValue = { breadcrumb };

  if (customData) value.custom = customData;

  sendInsiderPageView(EInsiderPageType.CATEGORY, value);
};

export const pushInsiderProduct = (product: IProductDetail) => {
  const insiderProduct = productDetailToInsiderProduct(product);

  sendInsiderPageView(EInsiderPageType.PRODUCT, insiderProduct);

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log('[Insider] Producto actualizado:', insiderProduct);
  }
};

export const pushInsiderCart = (products: Array<ICartProduct>, total: number, shippingCost = 0) => {
  const insiderCart = cartToInsiderCart(products, total, shippingCost);

  sendInsiderPageView(EInsiderPageType.BASKET, insiderCart);

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log('[Insider] Carrito actualizado:', insiderCart);
  }
};

export const pushInsiderTransaction = (orders: Array<IOrderCreated>) => {
  const insiderPurchase = orderDetailToInsiderPurchase(orders);

  sendInsiderPageView(EInsiderPageType.CONFIRMATION, insiderPurchase);

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log('[Insider] Transacción registrada:', insiderPurchase);
  }
};

export const pushInsiderOtherPage = (
  name: IInsiderOtherPageValue['name'],
  customData?: IInsiderOtherPageValue['custom'],
) => {
  const value: IInsiderOtherPageValue = { name };

  if (customData) value.custom = customData;

  sendInsiderPageView(EInsiderPageType.OTHER, value);
};

export const pushInsiderSearchPage = (query: string) => {
  pushInsiderOtherPage(EInsiderPageType.SEARCH, { search_term: query });
};

export const pushInsiderContentPage = () => {
  pushInsiderOtherPage(EInsiderPageType.CONTENT);
};

export const pushInsiderPromotionsPage = () => {
  pushInsiderOtherPage(EInsiderPageType.PROMOTIONS);
};

export const pushInsiderCheckoutShippingPage = () => {
  pushInsiderOtherPage(EInsiderPageType.CHECKOUT_SHIPPING);
};

export const pushInsiderCheckoutPurchasePage = () => {
  pushInsiderOtherPage(EInsiderPageType.CHECKOUT_PURCHASE);
};

export const triggerInsiderAddToCart = (product: IBaseProduct, quantity: number) => {
  const insiderProduct = baseProductToInsiderProduct(product);

  pushToInsiderQueue({
    type: EInsiderEvents.ADD_TO_CART,
    value: {
      ...insiderProduct,
      quantity,
    },
  });

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log('[Insider] Evento add_to_cart:', insiderProduct);
  }
};

export const triggerInsiderRemoveFromCart = (product: IBaseProduct, quantity: number) => {
  const insiderProduct = baseProductToInsiderProduct(product);

  pushToInsiderQueue({
    type: EInsiderEvents.REMOVE_FROM_CART,
    value: {
      ...insiderProduct,
      quantity,
    },
  });

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log('[Insider] Evento remove_from_cart:', insiderProduct);
  }
};

export const triggerInsiderCustomEvent = (eventName: string, parameters?: Record<string, any>) => {
  pushToInsiderQueue({
    type: 'custom_event',
    value: [
      {
        event_name: eventName,
        event_parameters: parameters || {},
      },
    ],
  });

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log(`[Insider] Evento personalizado "${eventName}":`, parameters);
  }
};

export const triggerInsiderSearchPreviewItemClick = (parameters: IInsiderSearchPreviewItemClickValue) => {
  triggerInsiderCustomEvent(EInsiderEvents.SEARCH_PREVIEW_ITEM_CLICK, parameters);

  if (isLocalEnv) {
    // eslint-disable-next-line no-console
    console.log('[Insider] Evento search_preview_item_click:', parameters);
  }
};

export const insertInsiderScript = async (onFinal: (success: boolean) => void) => {
  let loader = false;

  if (!loader) {
    loader = true;
    return new Promise<boolean>(resolve => {
      const url = `https://${INSIDER_URL}/ins.js?id=${INSIDER_ID}?v=${__APP_VERSION__}`;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = url;
      script.onload = () => {
        if (isLocalEnv) {
          // eslint-disable-next-line no-console
          console.log('[Insider] Script cargado exitosamente');
        }
        onFinal(true);
        resolve(true);
      };

      script.onerror = () => {
        // eslint-disable-next-line no-console
        console.error('[Insider] Error al cargar el script');
        onFinal(false);
        resolve(false);
      };

      document.body.appendChild(script);
    });
  }
  onFinal(true);
  return Promise.resolve(true);
};
